/* eslint-disable class-methods-use-this */
import { AxiosPromise, AxiosResponse } from 'axios';
import { http } from 'SharedDir/backend/axios/http';

import { ISSUE_ENDPOINTS } from 'SharedDir/backend/endpoints/issue';
import REPORTS from 'SharedDir/backend/endpoints/reports';
import { IdSlugMapping } from 'SharedDir/common/composables/useIssueDslScope';
import { StatusResponse } from 'SharedDir/declarations/endpoint';
import { FieldValueSet } from 'SharedDir/declarations/fields';
import { NIssue } from 'SharedDir/declarations/issue';
import { Paginated, PaginationParams } from 'SharedDir/declarations/pagination';
import { NServiceRoute } from 'SharedDir/declarations/service-route';
import { AnyObject, StatusMessage, WithRelations } from 'SharedDir/declarations/shared';
import { IntervalStatistics } from 'SharedDir/declarations/statistics';
import fileDownloader from 'SharedDir/utils/file-downloader';
import { getMyTimeZone } from 'SharedDir/utils/helpers';

import IssueDocument = NIssue.IssueDocument;

export default class IssuesApiClass {
  /**
   * Постранично получаем заявления
   *
   * @template ExtraFields
   * @param {PaginationParams<ExtraFields>} params
   * @returns {Promise<Paginated<Required<WithRelations<NIssue.IssueBare>>, { filters: ExtraFields}>>}
   * @memberof IssuesApi
   */
  async getPaginated<ExtraFields extends AnyObject = AnyObject>(
    params: PaginationParams<ExtraFields>
  ): Promise<Paginated<Required<WithRelations<NIssue.IssueBare>>, { filters: ExtraFields }>> {
    const { data } = (await http({
      ...ISSUE_ENDPOINTS.LIST,
      params,
    })) as AxiosResponse<
      Paginated<Required<WithRelations<NIssue.IssueBare>>, { filters: ExtraFields }>
    >;
    return data;
  }

  /**
   * Создаем черновик заявления
   *
   * @param {NIssue.DraftData} data
   * @returns {Promise<NIssue.Issue>}
   * @memberof IssuesApi
   */
  async create(data: NIssue.DraftData): Promise<NIssue.Issue> {
    const { data: issue } = await (http({
      ...ISSUE_ENDPOINTS.CREATE,
      data,
    }) as AxiosPromise<NIssue.Issue>);
    return issue;
  }

  /**
   * Получение данных о конкретном заявлении вместе
   * со значениями полей сгруппированных
   * по **ВСЕМ!!** секциям **ВСЕХ!!** этапов
   *
   * @param {number} id
   * @returns {Promise<NIssue.Issue>}
   * @memberof IssuesApi
   */
  async get(id: number): Promise<NIssue.Issue> {
    const { data } = await (http({ ...ISSUE_ENDPOINTS.GET(id) }) as AxiosPromise<NIssue.Issue>);
    return data;
  }

  /**
   * Получение значений полей
   * конкретного этапа сгруппированных по секциям
   *
   * @param {number} issueId
   * @param {number} stepId - идентификатор этапа
   * @returns {Promise<Array<NIssue.StepValues>>}
   * @memberof IssuesApi
   */
  async getStepValues(
    issueId: number,
    stepId: number
  ): Promise<{
    stepValues: Array<NIssue.StepValues>;
    valueScope: FieldValueSet;
    fieldIdSlugMapping: IdSlugMapping;
  }> {
    const { data } = await (http({
      ...ISSUE_ENDPOINTS.GET_STEP_VALUES(issueId, stepId),
    }) as AxiosPromise<{
      stepValues: Array<NIssue.StepValues>;
      valueScope: FieldValueSet;
      fieldIdSlugMapping: IdSlugMapping;
    }>);

    return data;
  }

  /**
   * Заполнении информации заявления по секциям
   *
   * @param {number} issueId
   * @param {number} sectionId
   * @param {FieldValueSet} fieldValues
   * @returns {Promise<NIssue.Issue>}
   * @memberof IssuesApi
   */
  async saveSectionValues(
    issueId: number,
    sectionId: number,
    fieldValues: FieldValueSet
  ): Promise<NIssue.Issue> {
    console.log(fieldValues);
    const { data } = await (http({
      ...ISSUE_ENDPOINTS.FILL_ISSUE_SECTION(issueId, sectionId),
      data: { fieldValues },
    }) as AxiosPromise<NIssue.Issue>);
    return data;
  }

  /**
   * Продвижение заявления по бизнес процессу.
   * Смена шага доступна в рамках маршрута, который используется для данного заявления.
   *
   * @param {number} issueId
   * @param {number} transitionId - идентификатор перехода
   * @param {IssueMoveData} moveData - идентификатор перехода
   * @returns {Promise<StatusMessage>}
   * @memberof IssuesApi
   */
  async move(
    issueId: number,
    transitionId?: number,
    moveData?: NIssue.IssueMoveData
  ): Promise<StatusMessage> {
    const { data } = await (http({
      ...ISSUE_ENDPOINTS.MOVE(issueId, transitionId),
      data: { ...moveData },
    }) as AxiosPromise<StatusMessage>);
    return data;
  }

  /**
   * Отмена заявления
   *
   * @param {number} issueId
   * @returns {Promise<NIssue.Issue>}
   * @memberof IssuesApi
   */
  async cancel(issueId: number): Promise<NIssue.Issue> {
    const { data } = await (http({
      ...ISSUE_ENDPOINTS.CANCEL(issueId),
    }) as AxiosPromise<NIssue.Issue>);
    return data;
  }

  /**
   * Отмена заявления заявителем
   *
   * @param {number} issueId
   * @returns {Promise<NIssue.Issue>}
   * @memberof IssuesApi
   */
  async cancelByIssuer(issueId: number): Promise<NIssue.Issue> {
    const { data } = await (http({
      ...ISSUE_ENDPOINTS.CANCEL_BY_ISSUER(issueId),
    }) as AxiosPromise<NIssue.Issue>);
    return data;
  }

  /**
   * Удаление заявления вместе с заявками ТМЦ
   *
   * @param {number} issueId
   * @returns {Promise<StatusResponse>}
   * @memberof IssuesApi
   */
  async deleteWithTmc(issueId: number): Promise<StatusResponse> {
    const { data } = await (http({
      ...ISSUE_ENDPOINTS.DELETE_WITH_TMC(issueId),
    }) as AxiosPromise<StatusResponse>);
    return data;
  }

  /**
   * Получение маршрута услуги
   *
   * @param {number} routeId
   * @returns {Promise<NIssue.Route>}
   * @memberof IssuesApi
   */
  async getRoute(routeId: number): Promise<NIssue.Route> {
    const { data } = await (http({
      ...ISSUE_ENDPOINTS.GET_ROUTE(routeId),
    }) as AxiosPromise<NIssue.Route>);
    return data;
  }

  /**
   * Получение секции вместе с полями перикрепленными к ним
   * для определенного этапа
   *
   * @param {number} stepId - идентификатор этапа
   * @returns {Promise<Array<NIssue.Section>>}
   * @memberof IssuesApi
   */
  async getSections(stepId: number): Promise<Array<NIssue.Section>> {
    const {
      data: { data: sections },
    } = await (http({
      ...ISSUE_ENDPOINTS.GET_SECTIONS,
      params: { stepId },
    }) as AxiosPromise<Paginated<NIssue.Section>>);
    return sections;
  }

  /**
   * Получение данных необходимых для карточки заявления
   *
   * @param issueId
   * @returns
   */
  async getCardInfo(issueId: number): Promise<NIssue.IssueCard> {
    const { data } = await (http({
      ...ISSUE_ENDPOINTS.GET(issueId),
    }) as AxiosPromise<NIssue.IssueCard>);
    return data;
  }

  /**
   * Получение истории переходов заявления по шагам
   *
   * @param issueId
   * @returns
   */
  async getStepChanges(issueId: number): Promise<NIssue.IssueStepChanges> {
    const { data } = await (http({
      ...ISSUE_ENDPOINTS.GET_STEP_CHANGES(issueId),
    }) as AxiosPromise<NIssue.IssueStepChanges>);
    return data;
  }

  /**
   * Получение истории дедлайнов
   *
   * @param issueId
   * @returns
   */
  async getDeadlines(issueId: number): Promise<NIssue.IssueDeadlines> {
    const { data } = await (http({
      ...ISSUE_ENDPOINTS.GET_DEADLINES(issueId),
    }) as AxiosPromise<NIssue.IssueDeadlines>);

    return data;
  }

  /**
   * Скачать карточку заявления
   *
   * @param issueId
   * @param params
   * @returns
   */
  async downloadReportCard(
    issueId: number,
    params?: NIssue.ReportCardParams
  ): Promise<AxiosResponse> {
    return http({
      ...REPORTS.GET_REPORT_ISSUE_STEP_CHANGES(issueId),
      responseType: 'blob',
      params: {
        ...(params || { timezone: getMyTimeZone() }),
      },
    }) as AxiosPromise<AxiosResponse>;
  }

  /**
   * Скачать заполненное заявление по шаблону
   *
   * @param issueId
   * @param templateId
   * @param reexport
   * @param exportFormat
   * @param userCertificateId
   * @returns
   */
  public async exportFilledIssueTemplate(
    issueId: number,
    templateId: number,
    reexport: boolean = false,
    exportFormat: NServiceRoute.ExportFileType = 'template_filetype',
    userCertificateId: number | null = null
  ) {
    const { data } = (await http({
      ...ISSUE_ENDPOINTS.EXPORT_FILLED_TEMPLATE(issueId, templateId),
      params: { reexport, exportFormat, userCertificateId },
    })) as AxiosResponse<NIssue.IssueDocument>;

    return data;
  }

  /**
   * Запрашиваем заявления по данному проекту
   * @param {string} projectId
   * @returns {Promise<void>}
   */
  async getForProject(projectId: string) {
    const params = {
      projectId,
      page: 1,
      rowsPerPage: 0,
      sortBy: 'id',
      descending: false,
    };
    const { data } = await this.getPaginated(params);
    return data;
  }

  async getMine<T extends Record<string, string | number | boolean | null>>(
    params?: T
  ): Promise<Array<Required<WithRelations<NIssue.IssueBare>>>> {
    const { data } = await this.getPaginated({
      rowsPerPage: 0,
      page: 1,
      myIssues: true,
      sortBy: 'createdAt',
      descending: true,
      ...(params || {}),
    });
    return data;
  }

  async getMinePortal<T extends Record<string, string | number | boolean | null>>(
    params?: T
  ): Promise<NIssue.IssuesMinePortalResponse> {
    const { data } = await http({
      ...ISSUE_ENDPOINTS.LIST_PORTAL,
      params: {
        rowsPerPage: 0,
        page: 1,
        sortBy: 'createdAt',
        descending: true,
        ...(params || {}),
      },
    });
    return data;
  }

  async joinChat(issueId: number): Promise<StatusResponse> {
    const { data } = await http(ISSUE_ENDPOINTS.JOIN_CHAT(issueId));
    return data;
  }

  // async getIssueDocuments(
  //   issueId: number
  // ): Promise<NIssue.IssuesMinePortalDocumentRequest> {
  //   const { data } = await http(ISSUE_ENDPOINTS.DOCUMENT(issueId));
  //   return data;
  // }

  /**
   * Общий отчет по мерам поддержки всех институтов развития в формате Excel
   *
   * @param {{fromDate: string, toDate: string}} params
   * @returns {Promise<any>}
   */
  async getReportForAll(params: { fromDate: string; toDate: string }) {
    return http({ ...ISSUE_ENDPOINTS.REPORT_FOR_ALL, params, responseType: 'blob' });
  }

  /**
   * Общий отчет по мерам поддержки указанного института развития формате Excel
   *
   * @param {number} departmentId
   * @param {{fromDate: string, toDate: string}} params
   * @returns {Promise<any>}
   */
  async getReportForSpecific(departmentId: number, params: { fromDate: string; toDate: string }) {
    return http({
      ...ISSUE_ENDPOINTS.REPORT_FOR_SPECIFIC(departmentId),
      params,
      responseType: 'blob',
    });
  }

  async getWithHistory(id: number) {
    const [data, history] = await Promise.all([this.get(id), this.getStepChanges(id)]);
    return { ...history, ...data };
  }

  async getIntervalStatistics(
    params: IntervalStatistics.IssueFilters
  ): Promise<IntervalStatistics.IssueResponse> {
    const { data } = await http({
      ...ISSUE_ENDPOINTS.INTERVAL_STATISTICS,
      params,
    });
    return data;
  }

  async exportIntervalStatistics(params: IntervalStatistics.IssueFilters): Promise<void> {
    fileDownloader.saveFileFromResponse(
      await http({
        ...ISSUE_ENDPOINTS.INTERVAL_STATISTICS_EXPORT,
        params,
        responseType: 'blob',
      })
    );
  }
}
